/**
 * Every enemy on screen in one draw call.
 *
 * A single `InstancedMesh` over a unit quad carries three kinds of instance:
 * the creature sprite, its 1px health pip bar, and up to two status badges.
 * They share one geometry, one material and one atlas, so a screen holding two
 * thousand swarmlings costs the same number of draw calls as a screen holding
 * one grunt.
 *
 * Two rules drive the shader:
 *
 *  1. The quad's top-left corner is rounded to a whole buffer pixel in the
 *     vertex shader. A sprite that sits on x = 41.3 for one frame and x = 41.7
 *     the next shimmers, and a shimmering pixel sprite reads as a rendering
 *     bug rather than as art. Positions arrive interpolated and leave snapped.
 *
 *  2. Fill and outline are tinted separately. The fill takes the enemy's
 *     phosphor above 1.0 so bloom carries its hue outward; the outline takes a
 *     near-black variant of the same hue so the silhouette punches a hole in
 *     the glowing lattice behind it. One 16×16 drawing therefore serves all
 *     eight enemy colours.
 *
 * `sync` allocates nothing. Every write lands in a preallocated typed array.
 */

import * as THREE from 'three';
import { SIM } from '@/core/config';
import { PHOSPHOR, INTENSITY, emissive, type PhosphorName } from '@/core/palette';
import type { Enemy, RenderSystem, World } from '@/core/types';
import {
  GLYPHS,
  GLYPH_FOR_FAMILY,
  STATUS_GLYPHS,
  SWARMLING_FACINGS,
  SWARMLING_TIER_FACINGS,
  isGlyphName,
  type GlyphName,
} from '@/assets/glyphs';
import {
  ATLAS_SIZE,
  RECT_COUNT,
  RECT_PX,
  rect,
  texture as atlasTexture,
  type GlyphRect,
} from '@/render/GlyphAtlas';

/** Sprite + bar + two badges. Sized for a worst-case swarm round. */
const MAX_ENEMIES = 2048;
const INSTANCES_PER_ENEMY = 4;
export const MAX_INSTANCES = MAX_ENEMIES * INSTANCES_PER_ENEMY;

/** `aExtra.x` discriminator. */
const KIND_SPRITE = 0;
const KIND_BAR = 1;

/** Badge colours, keyed by the order in `STATUS_GLYPHS`. */
const STATUS_COLOUR: readonly PhosphorName[] = ['yellow', 'red', 'green', 'cyan'];

// ──────────────────────────────────────────────────────────────── shaders ──

const VERT = /* glsl */ `
uniform vec4 uRects[${RECT_COUNT}];

attribute vec3 aOffset;   // centre in buffer pixels, z = depth
attribute vec2 aSize;     // quad extent in buffer pixels
attribute float aRect;    // index into uRects; ignored when aExtra.x > 0.5
attribute vec3 aColour;   // linear phosphor base
attribute vec4 aParams;   // intensity, flash, birth, rotation
attribute vec4 aExtra;    // kind, hpFrac, shieldFrac, cycle

varying vec2 vQuad;
varying vec4 vRect;
varying vec3 vColour;
varying vec4 vParams;
varying vec2 vRot;
varying vec3 vBar;
varying vec2 vSize;

void main() {
  vQuad = position.xy;
  vColour = aColour;
  vParams = aParams;
  vBar = aExtra.xyz;
  vSize = aSize;
  vRect = uRects[int(max(aRect, 0.0))];

  // Sixteen discrete steps. Free rotation resamples a pixel grid into porridge;
  // a quantised wheel keeps the texel lattice coherent frame to frame.
  float wedge = 6.28318531 / 16.0;
  float r = floor(aParams.w / wedge + 0.5) * wedge;
  vRot = vec2(cos(r), sin(r));

  // The whole point of the file: whole-pixel corners, always.
  vec2 corner = floor(aOffset.xy - aSize * 0.5 + 0.5);
  vec2 p = corner + position.xy * aSize;

  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, aOffset.z, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uAtlas;
uniform float uAtlasSize;
uniform vec3 uHpHigh;
uniform vec3 uHpMid;
uniform vec3 uHpLow;
uniform vec3 uShield;
uniform vec3 uEmpty;

varying vec2 vQuad;
varying vec4 vRect;
varying vec3 vColour;
varying vec4 vParams;
varying vec2 vRot;
varying vec3 vBar;
varying vec2 vSize;

float hash21(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  if (vBar.x > 0.5) {
    // Health pips. Two lit pixels, one dark, repeating across the bar. Drawn in
    // this pass rather than as DOM or a second mesh, so it costs nothing.
    float px = floor(vQuad.x * vSize.x);
    float slot = floor(px / 3.0);
    if (px - slot * 3.0 > 1.5) discard;

    float total = max(floor((vSize.x + 1.0) / 3.0), 1.0);
    bool lit = (slot + 1.0) / total <= vBar.y + 0.0001;

    vec3 c = vBar.y > 0.6 ? uHpHigh : (vBar.y > 0.3 ? uHpMid : uHpLow);
    if (vBar.z > 0.001) c = uShield;

    gl_FragColor = lit ? vec4(c, 1.0) : vec4(uEmpty, 0.9);
    return;
  }

  // Rotate the sample, not the quad: the quad stays axis-aligned on the pixel
  // grid and the artwork inside it turns in chunky steps.
  vec2 q = vQuad - 0.5;
  q = vec2(q.x * vRot.x + q.y * vRot.y, -q.x * vRot.y + q.y * vRot.x) + 0.5;
  if (q.x < 0.0 || q.x >= 1.0 || q.y < 0.0 || q.y >= 1.0) discard;

  vec2 texel = floor(q * vRect.zw);
  vec3 m = texture2D(uAtlas, (vRect.xy + texel + 0.5) / uAtlasSize).rgb;

  float fill = m.r;
  float outl = m.g;
  float det = m.b;
  if (fill + outl + det < 0.01) discard;

  // Spawn dissolve: texels appear in a stable scatter as birth runs 0 -> 1.
  float birth = vParams.z;
  if (birth < 0.999 && hash21(texel + vRect.xy) > birth) discard;

  // Drawn mass emits; the dark channel occludes. Hollowness belongs to the
  // *art*, not to this shader: assets/glyphs.ts draws the swarmling as a ring
  // with an authored dark core, so overlapping bodies stay countable because
  // each carries its own hole — the mechanism holding
  // gwre2_swarm-clusters-bloom.jpg together. Tinting the derived dilation ring
  // bright instead put the light *outside* every body, where neighbouring rings
  // touch and merge, and left the mass itself emitting at 0.22 rather than
  // dark. Both effects fed the porridge this was meant to fix.
  // Small bodies emit below the bloom threshold, big ones well above it.
  //
  // A swarmling and a boss carried the same 1.15 multiplier, so 460 swarmlings
  // fed the pyramid 460 times and the dense knot filled back in with their own
  // skirts — the porridge the hollow ring art exists to prevent. Scaling the
  // fill by body size puts a swarmling at 1.3 linear, just under the bloom
  // threshold: still a bright yellow pixel after the tonemap, but it
  // contributes nothing to the glow, so the holes between bodies survive.
  // Light in this game belongs to events and to things worth looking at.
  float inten = vParams.x;
  float fillGain = mix(0.60, 1.15, smoothstep(10.0, 26.0, vSize.x));
  vec3 fillCol = vColour * inten * fillGain;
  vec3 detCol = mix(vColour, vec3(1.0), 0.22) * inten * 2.0;
  // Near-black, but the enemy's own hue, so the silhouette punches a hole in
  // the lattice without reading as a grey sticker.
  vec3 outCol = vColour * inten * 0.06;

  vec3 c = outCol;
  float a = outl;
  c = mix(c, fillCol, fill);
  a = max(a, fill);
  c = mix(c, detCol, det);
  a = max(a, det);

  // Hit flash lifts the drawn mass and leaves the dark channel alone, so the
  // silhouette survives the hit instead of turning into a blob. Tinted toward
  // the enemy's own phosphor rather than a flat achromatic 2.8: a hit should
  // read as that creature flaring, which is what lets you tell what got hit in
  // a crowd.
  c = mix(c, mix(vColour, vec3(1.0), 0.6) * 3.0, clamp(vParams.y, 0.0, 1.0) * max(fill, det));

  // Rim flare while spawning.
  c += vec3(0.9, 1.1, 1.6) * (1.0 - birth) * outl * 2.0;

  gl_FragColor = vec4(c, a);
}
`;

// ─────────────────────────────────────────────────────── colour scratch ──
//
// Module-scope scratch instead of a returned tuple. `sync` runs every frame
// over thousands of enemies; a per-enemy array literal is a per-frame garbage
// pause you can see.

let sr = 0;
let sg = 0;
let sb = 0;
let si = 1;

function resolveColour(name: PhosphorName, isBoss: boolean, time: number): void {
  const base = PHOSPHOR[name];
  sr = base[0];
  sg = base[1];
  sb = base[2];
  si = INTENSITY[name];

  if (!isBoss) return;

  // Bosses cycle slowly through the hot end of the palette so they read as a
  // different class of thing before the player reads their size.
  const m = PHOSPHOR.magenta;
  const y = PHOSPHOR.yellow;
  const t1 = 0.5 + 0.5 * Math.sin(time * 1.1);
  const t2 = 0.5 + 0.5 * Math.sin(time * 0.67 + 2.1);
  sr = sr + (m[0] - sr) * t1;
  sg = sg + (m[1] - sg) * t1;
  sb = sb + (m[2] - sb) * t1;
  sr = sr + (y[0] - sr) * t2 * 0.4;
  sg = sg + (y[1] - sg) * t2 * 0.4;
  sb = sb + (y[2] - sb) * t2 * 0.4;
  si = 3.4;
}

function glyphForEnemy(e: Enemy): GlyphName {
  const a = e.archetype;
  // `EnemyCatalog` may name a glyph directly; otherwise the family picks it.
  if (isGlyphName(a.glyph)) return a.glyph;
  if (a.family === 'swarmling') {
    /*
     * Two things to resolve from one string, so the string names the *set* and
     * the heading picks the facing out of it. `swarmling` is the base set and
     * also the fallback, which is what keeps an unrecognised tier drawing a
     * body rather than throwing on a name lookup mid-frame.
     */
    const set = SWARMLING_TIER_FACINGS[a.glyph] ?? SWARMLING_FACINGS;
    const n = set.length;
    const q = Math.round((e.heading / (Math.PI * 2)) * n) % n;
    return set[q < 0 ? q + n : q];
  }
  return GLYPH_FOR_FAMILY[a.family];
}

/** Bipeds stay upright. Directional bodies bank into vertical travel. */
function tiltForEnemy(e: Enemy): number {
  const f = e.archetype.family;
  if (f !== 'runner' && f !== 'weaver') return 0;
  return Math.sin(e.heading) * 0.42;
}

// ────────────────────────────────────────────────────────────── renderer ──

export class EnemyRenderer implements RenderSystem {
  readonly name = 'EnemyRenderer';

  /** Add this to the scene. Nothing else about the mesh is public. */
  readonly object: THREE.Object3D;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly mesh: THREE.InstancedMesh;

  private readonly aOffset: THREE.InstancedBufferAttribute;
  private readonly aSize: THREE.InstancedBufferAttribute;
  private readonly aRect: THREE.InstancedBufferAttribute;
  private readonly aColour: THREE.InstancedBufferAttribute;
  private readonly aParams: THREE.InstancedBufferAttribute;
  private readonly aExtra: THREE.InstancedBufferAttribute;

  private readonly fOffset: Float32Array;
  private readonly fSize: Float32Array;
  private readonly fRect: Float32Array;
  private readonly fColour: Float32Array;
  private readonly fParams: Float32Array;
  private readonly fExtra: Float32Array;

  private count = 0;

  constructor() {
    // Unit quad in (fx, fy), y measured downward from the sprite's top edge so
    // it lines up with buffer space and with atlas rows. The camera performs
    // the single Y flip; nothing here does a second one.
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(
        new Float32Array([0, 0, 0, 1, 0, 0, 1, 1, 0, 0, 1, 0]),
        3,
      ),
    );
    geometry.setIndex([0, 1, 2, 0, 2, 3]);

    this.fOffset = new Float32Array(MAX_INSTANCES * 3);
    this.fSize = new Float32Array(MAX_INSTANCES * 2);
    this.fRect = new Float32Array(MAX_INSTANCES);
    this.fColour = new Float32Array(MAX_INSTANCES * 3);
    this.fParams = new Float32Array(MAX_INSTANCES * 4);
    this.fExtra = new Float32Array(MAX_INSTANCES * 4);

    this.aOffset = new THREE.InstancedBufferAttribute(this.fOffset, 3);
    this.aSize = new THREE.InstancedBufferAttribute(this.fSize, 2);
    this.aRect = new THREE.InstancedBufferAttribute(this.fRect, 1);
    this.aColour = new THREE.InstancedBufferAttribute(this.fColour, 3);
    this.aParams = new THREE.InstancedBufferAttribute(this.fParams, 4);
    this.aExtra = new THREE.InstancedBufferAttribute(this.fExtra, 4);

    this.aOffset.setUsage(THREE.DynamicDrawUsage);
    this.aSize.setUsage(THREE.DynamicDrawUsage);
    this.aRect.setUsage(THREE.DynamicDrawUsage);
    this.aColour.setUsage(THREE.DynamicDrawUsage);
    this.aParams.setUsage(THREE.DynamicDrawUsage);
    this.aExtra.setUsage(THREE.DynamicDrawUsage);

    geometry.setAttribute('aOffset', this.aOffset);
    geometry.setAttribute('aSize', this.aSize);
    geometry.setAttribute('aRect', this.aRect);
    geometry.setAttribute('aColour', this.aColour);
    geometry.setAttribute('aParams', this.aParams);
    geometry.setAttribute('aExtra', this.aExtra);
    this.geometry = geometry;

    // No explicit `glslVersion`. Three compiles every non-raw ShaderMaterial as
    // `#version 300 es` regardless, and the default path keeps its GLSL1
    // compatibility defines — including `gl_FragColor`, which the GLSL3 path
    // drops. So the source below is GLSL1-flavoured and the program is ES 3.00,
    // which is what the dynamic `uRects[i]` lookup in the vertex shader needs.
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uRects: { value: RECT_PX },
        uAtlas: { value: atlasTexture },
        uAtlasSize: { value: ATLAS_SIZE },
        uHpHigh: { value: new THREE.Vector3().fromArray(emissive('lime', 0.55)) },
        uHpMid: { value: new THREE.Vector3().fromArray(emissive('yellow', 0.5)) },
        uHpLow: { value: new THREE.Vector3().fromArray(emissive('red', 0.6)) },
        uShield: { value: new THREE.Vector3().fromArray(emissive('cyan', 0.55)) },
        uEmpty: { value: new THREE.Vector3().fromArray(emissive('gray', 0.5)) },
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NormalBlending,
      toneMapped: false,
      // The playfield camera flips Y once, which inverts the quad's winding.
      // This renderer does not own that camera, so it refuses to care which way
      // round it is.
      side: THREE.DoubleSide,
    });

    this.mesh = new THREE.InstancedMesh(this.geometry, this.material, MAX_INSTANCES);
    this.mesh.name = 'enemies';
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.renderOrder = 20;
    this.object = this.mesh;
  }

  sync(world: World, alpha: number): void {
    const enemies = world.enemies;
    const time = world.time;
    const lead = alpha * SIM.dt;
    let n = 0;

    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (!e.alive) continue;
      if (n + INSTANCES_PER_ENEMY > MAX_INSTANCES) break;

      const a = e.archetype;
      const r: GlyphRect = rect(glyphForEnemy(e));

      // The sim stores state at the last completed step; extrapolate forward by
      // the render fraction. Wall time never enters here.
      const x = e.pos.x + e.vel.x * lead;
      const y = e.pos.y + e.vel.y * lead;

      const isBoss = a.family === 'boss';
      resolveColour(a.colour, isBoss, time);

      // ── creature sprite
      let o3 = n * 3;
      let o4 = n * 4;
      this.fOffset[o3] = x;
      this.fOffset[o3 + 1] = y;
      this.fOffset[o3 + 2] = 0;
      this.fSize[n * 2] = r.w;
      this.fSize[n * 2 + 1] = r.h;
      this.fRect[n] = r.index;
      this.fColour[o3] = sr;
      this.fColour[o3 + 1] = sg;
      this.fColour[o3 + 2] = sb;
      this.fParams[o4] = si;
      this.fParams[o4 + 1] = e.flash < 0 ? 0 : e.flash > 1 ? 1 : e.flash;
      this.fParams[o4 + 2] = e.birth > 1 ? 1 : e.birth < 0 ? 0 : e.birth;
      this.fParams[o4 + 3] = tiltForEnemy(e);
      this.fExtra[o4] = KIND_SPRITE;
      this.fExtra[o4 + 1] = 0;
      this.fExtra[o4 + 2] = 0;
      this.fExtra[o4 + 3] = 0;
      n++;

      // ── health pips, one pixel tall, tucked under the feet
      const hpFrac = e.maxHp > 0 ? clamp01(e.hp / e.maxHp) : 0;
      const shieldFrac = a.shield > 0 ? clamp01(e.shield / a.shield) : 0;
      const barY = y + r.h * 0.5 + 1.5;

      o3 = n * 3;
      o4 = n * 4;
      this.fOffset[o3] = x;
      this.fOffset[o3 + 1] = barY;
      this.fOffset[o3 + 2] = 0;
      // Trim to a whole number of pips so the bar never ends in a 1px runt:
      // 16px art gives five 2px pips with 1px gaps, i.e. 14px of bar.
      this.fSize[n * 2] = Math.floor((r.art + 1) / 3) * 3 - 1;
      this.fSize[n * 2 + 1] = 1;
      this.fRect[n] = 0;
      this.fColour[o3] = 1;
      this.fColour[o3 + 1] = 1;
      this.fColour[o3 + 2] = 1;
      this.fParams[o4] = 1;
      this.fParams[o4 + 1] = 0;
      this.fParams[o4 + 2] = 1;
      this.fParams[o4 + 3] = 0;
      this.fExtra[o4] = KIND_BAR;
      this.fExtra[o4 + 1] = hpFrac;
      this.fExtra[o4 + 2] = shieldFrac;
      this.fExtra[o4 + 3] = 0;
      n++;

      // ── status badges, at most two, floating above the head
      let dotKind = 0; // 1 = burning, 2 = corroded
      for (let d = 0; d < e.dots.length; d++) {
        const dot = e.dots[d];
        if (dot.until <= time) continue;
        if (dot.type === 'corrosive') dotKind |= 2;
        else dotKind |= 1;
      }
      const stunned = e.stunUntil > time;
      const slowed = e.slowUntil > time;
      const badges =
        (stunned ? 1 : 0) +
        ((dotKind & 1) !== 0 ? 1 : 0) +
        ((dotKind & 2) !== 0 ? 1 : 0) +
        (slowed ? 1 : 0);

      if (badges > 0) {
        const shown = badges > 2 ? 2 : badges;
        const badgeY = y - r.h * 0.5 - 6;
        let placed = 0;
        for (let s = 0; s < STATUS_GLYPHS.length && placed < shown; s++) {
          const on =
            s === 0 ? stunned :
            s === 1 ? (dotKind & 1) !== 0 :
            s === 2 ? (dotKind & 2) !== 0 :
            slowed;
          if (!on) continue;

          const br = rect(STATUS_GLYPHS[s]);
          const cName = STATUS_COLOUR[s];
          const cRgb = PHOSPHOR[cName];
          o3 = n * 3;
          o4 = n * 4;
          this.fOffset[o3] = x + (placed - (shown - 1) * 0.5) * 9;
          this.fOffset[o3 + 1] = badgeY;
          this.fOffset[o3 + 2] = 0;
          this.fSize[n * 2] = br.w;
          this.fSize[n * 2 + 1] = br.h;
          this.fRect[n] = br.index;
          this.fColour[o3] = cRgb[0];
          this.fColour[o3 + 1] = cRgb[1];
          this.fColour[o3 + 2] = cRgb[2];
          this.fParams[o4] = INTENSITY[cName] * 0.8;
          this.fParams[o4 + 1] = 0;
          this.fParams[o4 + 2] = 1;
          this.fParams[o4 + 3] = 0;
          this.fExtra[o4] = KIND_SPRITE;
          this.fExtra[o4 + 1] = 0;
          this.fExtra[o4 + 2] = 0;
          this.fExtra[o4 + 3] = 0;
          n++;
          placed++;
        }
      }
    }

    this.count = n;
    this.mesh.count = n;
    this.upload(this.aOffset, n);
    this.upload(this.aSize, n);
    this.upload(this.aRect, n);
    this.upload(this.aColour, n);
    this.upload(this.aParams, n);
    this.upload(this.aExtra, n);
  }

  /** Uploads only the instances written this frame, not the whole pool. */
  private upload(attr: THREE.InstancedBufferAttribute, n: number): void {
    attr.clearUpdateRanges();
    if (n > 0) attr.addUpdateRange(0, n * attr.itemSize);
    attr.needsUpdate = true;
  }

  /** Instances issued by the last `sync`. Sprite + bar + badges, not enemies. */
  get instanceCount(): number {
    return this.count;
  }

  dispose(): void {
    this.object.removeFromParent();
    this.mesh.dispose();
    this.geometry.dispose();
    this.material.dispose();
    // The atlas texture is a shared module singleton. Whoever tears down the
    // app calls `disposeAtlas()`; a single renderer must not pull it out from
    // under the others.
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Re-exported so `main.ts` can register a sprite-sheet scenario without digging. */
export { GLYPHS };
